import React from 'react';
import Divider from '@mui/material/Divider';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  ListItemButton,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Add as AddIcon,
  List as ListIcon,
  CheckCircle as CheckCircleIcon,
  BarChart as BarChartIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Groups as GroupsIcon,
  SwapHoriz as SwapHorizIcon,
  History as HistoryIcon,
  Help as HelpIcon,
  Lightbulb as LightbulbIcon,
  Link as LinkIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MobileBottomNav from '../components/MobileBottomNav';

export default function MobileLayout({ children, session }) {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, signOut } = useAuth();

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
    handleClose();
  };

    const getNavItems = () => {
    if (!profile) return [];
    
    const baseItems = [
        { label: 'Dashboard', icon: <HomeIcon />, path: '/dashboard' },
    ];

    if (profile.rol === 'elev') {
      return [
        ...baseItems,
        { label: 'Cerere Nouă', icon: <AddIcon />, path: '/new-request' },
        { label: 'Cererile Mele', icon: <ListIcon />, path: '/my-requests' },
        { label: 'Sugestii', icon: <LightbulbIcon />, path: '/suggestions' },
        { label: 'Integrare cu SRBHub', icon: <LinkIcon />, path: '/profil/integrari' },
        // { label: 'Beneficii', icon: <CardGiftcardIcon />, path: '/benefits' }, // Temporar dezactivat
        { label: 'Ghid', icon: <HelpIcon />, path: '/guide' },
      ];
    }

    if (profile.rol === 'profesor') {
      return [
        ...baseItems,
        { label: 'Elevii Mei', icon: <GroupsIcon />, path: '/teacher-students' },
        { label: 'Validare Cereri', icon: <CheckCircleIcon />, path: '/review-requests' },
        { label: 'Statistici', icon: <BarChartIcon />, path: '/intelligence' },
        { label: 'Ghid', icon: <HelpIcon />, path: '/guide' },
      ];
    }

    if (profile.rol === 'coordonator') {
      return [
        ...baseItems,
        { label: 'Validare Cereri', icon: <CheckCircleIcon />, path: '/review-requests' },
        { label: 'Sistem Inteligent', icon: <BarChartIcon />, path: '/intelligence' },
        { label: 'Toate cererile', icon: <ListIcon />, path: '/coordonator/cereri' },
        { label: 'Mutare Clasă', icon: <SwapHorizIcon />, path: '/class-management' },
        { label: 'Integrări SRBHub', icon: <LinkIcon />, path: '/coordonator/integrari' },
        { label: 'Audit Integrări', icon: <FactCheckIcon />, path: '/coordonator/audit-integrari' },
        { label: 'Jurnal Audit', icon: <HistoryIcon />, path: '/audit-log' },
        { label: 'Ghid', icon: <HelpIcon />, path: '/guide' },
      ];
    }

    return baseItems;
    };

  const navItems = getNavItems();

  const drawerList = () => (
    <Box
      sx={{ width: 280 }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      {/* Header cu logo */}
      <Box 
        sx={{ 
          p: 2.5, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
          background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
        }}
      >
        <Box
          component="img"
          src="/SiglaLiceuRem.png"
          alt="Sigla Liceului"
          sx={{ width: 40, height: 40, objectFit: 'contain' }}
        />
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, color: 'white' }}>
            SRB Credite
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            Școala Romano-Britanică
          </Typography>
        </Box>
      </Box>
      
      {/* User info */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'grey.50' }}>
        <Avatar 
          sx={{ 
            bgcolor: 'primary.main',
            width: 44,
            height: 44,
            fontWeight: 600,
          }}
        >
          {profile?.prenume?.[0]}{profile?.nume?.[0]}
        </Avatar>
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>
            {profile?.prenume} {profile?.nume}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
            {profile?.rol} {profile?.clasa ? `• ${profile.clasa}` : ''}
          </Typography>
        </Box>
      </Box>
      <Divider />
      
      {/* Navigation */}
      <Box sx={{ p: 1.5 }}>
        <Typography 
          variant="overline" 
          color="text.secondary" 
          sx={{ px: 1.5, fontSize: '0.65rem', letterSpacing: 1 }}
        >
          Navigare
        </Typography>
        <List sx={{ mt: 0.5 }}>
          {navItems.map((item) => {
            const isSelected = location.pathname === item.path;
            return (
              <ListItemButton
                key={item.label}
                selected={isSelected}
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  '&.Mui-selected': {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                    '& .MuiListItemText-primary': { color: 'primary.main', fontWeight: 600 },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: isSelected ? 600 : 500 }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>
      
      <Divider sx={{ mx: 2 }} />
      
      {/* Account actions */}
      <Box sx={{ p: 1.5 }}>
        <Typography 
          variant="overline" 
          color="text.secondary" 
          sx={{ px: 1.5, fontSize: '0.65rem', letterSpacing: 1 }}
        >
          Cont
        </Typography>
        <List sx={{ mt: 0.5 }}>
          <ListItemButton 
            onClick={() => navigate('/profile')}
            sx={{ borderRadius: 2, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}><PersonIcon /></ListItemIcon>
            <ListItemText primary="Profilul meu" primaryTypographyProps={{ fontSize: '0.9rem' }} />
          </ListItemButton>
          
          <ListItemButton 
            onClick={handleLogout}
            sx={{ borderRadius: 2, color: 'error.main' }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}><LogoutIcon /></ListItemIcon>
            <ListItemText primary="Deconectare" primaryTypographyProps={{ fontSize: '0.9rem' }} />
          </ListItemButton>
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', pb: 7 }}>
      {/* Top AppBar */}
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer(true)}
            sx={{ 
              mr: 2,
              borderRadius: 2,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {location.pathname === '/dashboard' && 'Dashboard'}
              {location.pathname === '/new-request' && 'Cerere Noua'}
              {location.pathname === '/my-requests' && 'Cererile Mele'}
              {location.pathname === '/review-requests' && 'Validare Cereri'}
              {location.pathname === '/intelligence' && 'Sistem Inteligent'}
              {location.pathname === '/teacher-students' && 'Elevii Mei'}
              {location.pathname === '/profile' && 'Profilul Meu'}
              {location.pathname === '/class-management' && 'Mutare Clasa'}
              {location.pathname === '/audit-log' && 'Jurnal Audit'}
              {location.pathname === '/guide' && 'Ghid'}
              {location.pathname.startsWith('/student/') && 'Detalii Elev'}
            </Typography>
          </Box>
          
          <IconButton 
            onClick={handleMenu} 
            sx={{ 
              p: 0.5,
              border: '2px solid',
              borderColor: 'divider',
            }}
          >
            <Avatar 
              sx={{ 
                width: 32, 
                height: 32, 
                bgcolor: 'primary.main',
                fontSize: '0.8rem',
                fontWeight: 600,
              }}
            >
              {profile?.prenume?.[0]}{profile?.nume?.[0]}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Side Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: { borderRadius: '0 16px 16px 0' }
        }}
      >
        {drawerList()}
      </Drawer>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{ sx: { mt: 1, minWidth: 200 } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {profile?.prenume} {profile?.nume}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {profile?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => { navigate('/profile'); handleClose(); }} sx={{ gap: 1.5 }}>
          <PersonIcon fontSize="small" color="action" />
          <Typography variant="body2">Profilul meu</Typography>
        </MenuItem>
        <MenuItem onClick={handleLogout} sx={{ gap: 1.5, color: 'error.main' }}>
          <LogoutIcon fontSize="small" />
          <Typography variant="body2">Deconectare</Typography>
        </MenuItem>
      </Menu>

      {/* Main Content */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: { xs: 1.5, sm: 3 }, 
          pt: 9,
          bgcolor: 'background.default',
        }}
        className="animate-fade-in-up"
      >
        {children}
      </Box>

      {/* Bottom Navigation (doar pentru user autentificat) */}
      {profile && <MobileBottomNav userRole={profile.rol} />}
    </Box>
  );
}

